import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiRequest, setToken } from '@/lib/api';
import i18n, { ensureLangLoaded, SUPPORTED_LANGS, type LangCode } from '@/i18n';

/**
 * Atterrissage du SSO Mars (Lot A 2026-07-20).
 * mars-hub redirige ici avec ?sso_token=… ; on l'échange contre une session
 * Network-iA (POST /auth/sso/mars) puis on file au tableau de bord.
 */
export default function SsoCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [error, setError] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const ssoToken = params.get('sso_token');
    if (!ssoToken) {
      setError(true);
      return;
    }
    apiRequest<{ token: string; user?: { preferred_lang?: string | null } }>('/auth/sso/mars', {
      method: 'POST',
      body: { sso_token: ssoToken },
      auth: false,
    })
      .then(async (res) => {
        setToken(res.token);
        // Lot 1 — l'interface bascule sur la langue du compte (celle du CRM
        // Mars), au lieu de laisser le navigateur décider.
        const lang = res.user?.preferred_lang ?? '';
        if (SUPPORTED_LANGS.some((l) => l.code === lang)) {
          localStorage.setItem('nia-lang', lang);
          await ensureLangLoaded(lang as LangCode);
          await i18n.changeLanguage(lang);
        }
        await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
        navigate('/', { replace: true });
      })
      .catch(() => setError(true));
  }, [params, navigate, qc]);

  return (
    <div className="flex min-h-screen items-center justify-center p-6 text-center">
      {error ? (
        <div>
          <p className="font-display text-lg font-800 text-ink">{t('sso.errorTitle')}</p>
          <p className="mt-2 text-sm text-ink/60">{t('sso.errorHint')}</p>
        </div>
      ) : (
        <p className="animate-pulse text-ink/70">{t('sso.connecting')}</p>
      )}
    </div>
  );
}
